import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { StudioProvider } from 'Contexts/StudioContext'
import { Studio } from 'Core/Studio'
import { AssignmentResponseType, getTaskFeedbackPreview } from 'Services/tasks.service'
import { PAGE_TITLES } from 'Constants/page.constants'
import { ContextType, TaskMode } from 'Types/Tasks.types'
import { useTitle } from 'Hooks/use-title'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import { useMessage } from 'shared/hooks/use-message'

type TaskFeedbackPreviewProps = {
    pageTitle?: (typeof PAGE_TITLES)[keyof typeof PAGE_TITLES]
    getDetails?: (taskId: string, userId: string) => Promise<AssignmentResponseType | undefined>
}

export const TaskFeedbackPreview = ({
    pageTitle = PAGE_TITLES.FEEDBACK_PREVIEW,
    getDetails = getTaskFeedbackPreview,
}: TaskFeedbackPreviewProps) => {
    const [studio, setStudio] = useState<Studio>()
    const { userProfile } = useUserProfile()
    const { taskId } = useParams()
    const [messageApi] = useMessage()

    useTitle(pageTitle)

    useEffect(() => {
        async function getTaskDetails() {
            if (!taskId || !userProfile.user.user_id) return
            try {
                const details = await getDetails(taskId, userProfile.user.user_id)

                if (!details) {
                    messageApi.error('Failed to load the task feedback preview. Please try again')
                    console.error('Failed to load the task feedback preview. Please try again')
                    return
                }
                setStudio(
                    new Studio({
                        taskType: details?.assignmentResp?.assignment.task.type,
                        interfaceKey: details?.assignmentResp?.assignment.interface_key,
                        taskConfig: {
                            taskId,
                            assignment: details?.assignmentResp?.assignment,
                            submissionId: 'MockSubmissionId',
                            mode: TaskMode.TaskViewer,
                        },
                        context: {
                            type: ContextType.NormalTask,
                        },
                        pluginsConfig: [],
                        onNextTask: () => {},
                        onExitTask: () => {},
                    })
                )
            } catch (error) {
                messageApi.error('Error fetching TaskPreview')
                console.error('Error fetching TaskPreview:', error)
            }
        }

        if (!studio) {
            getTaskDetails()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (!studio) {
        // might want to render a loading state here if needed
        return null
    }

    return (
        <div data-testid="task-preview-wrapper">
            <StudioProvider value={studio}>{studio.workbench.element}</StudioProvider>
        </div>
    )
}
